import React, { useEffect, useState } from "react";
import { Card, Button, Select, DatePicker, Space, Typography, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const REPORTS = [
  { key: "reportAttendance", label: "Monthly Attendance Report", desc: "All attendance sessions in the selected range." },
  { key: "reportLeave", label: "Leave Records Report", desc: "Leave requests and their decisions." },
  { key: "reportWfh", label: "WFH Records Report", desc: "Work-from-home requests and their decisions." },
  { key: "reportLateArrivals", label: "Late Arrivals Report", desc: "Sessions flagged as late logins." },
  { key: "reportEarlyDepartures", label: "Early Departures Report", desc: "Sessions flagged as early departures." },
  { key: "reportGeofenceViolations", label: "Geofence Violations Report", desc: "Geofence exits, auto-logouts and rejected logins." },
  { key: "reportAutoLogouts", label: "Auto Logout Events Report", desc: "Sessions auto-closed by the geofence/stale-session sweeper." },
  { key: "reportWorkingHours", label: "Employee Working Hours Report", desc: "Resolved effective working hours per employee." },
];

const Reports = () => {
  const { request } = useRequest();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({});
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    request({ url: apiPath.employees, method: "GET", onSuccess: (res) => setEmployees(res?.data?.employees || []) });
    request({ url: apiPath.departments, method: "GET", onSuccess: (res) => setDepartments(res?.data?.data || []) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDownload = async (reportKey, format = "xlsx") => {
    const token = localStorage.getItem("i_ems_admin_token") || "";
    const params = new URLSearchParams();
    if (filters.from) params.append("from", filters.from);
    if (filters.to) params.append("to", filters.to);
    if (filters.employee) params.append("employee", filters.employee);
    if (filters.department) params.append("department", filters.department);
    params.append("format", format);

    setDownloading(reportKey);
    try {
      const response = await axios.get(`${apiPath.baseURL}${apiPath[reportKey]}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${reportKey}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error("Could not download report");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <PageTransition>
      <PageHeader title="Reports" subtitle="Download attendance, leave, WFH and audit reports" />
      <Space style={{ marginBottom: 16 }} wrap>
        <RangePicker
          onChange={(dates, dateStrings) => setFilters((f) => ({ ...f, from: dateStrings[0] || undefined, to: dateStrings[1] || undefined }))}
        />
        <Select
          allowClear
          placeholder="Employee"
          style={{ width: 200 }}
          showSearch
          optionFilterProp="label"
          options={employees.map((e) => ({ value: e._id, label: e.name }))}
          onChange={(v) => setFilters((f) => ({ ...f, employee: v }))}
        />
        <Select
          allowClear
          placeholder="Department"
          style={{ width: 200 }}
          options={departments.map((d) => ({ value: d._id, label: d.name }))}
          onChange={(v) => setFilters((f) => ({ ...f, department: v }))}
        />
      </Space>

      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        {REPORTS.map((r) => (
          <Card key={r.key} size="small" className="i-ems-clickable-card">
            <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
              <div>
                <Text strong>{r.label}</Text>
                <br />
                <Text type="secondary">{r.desc}</Text>
              </div>
              <Space>
                <Button
                  icon={<DownloadOutlined />}
                  loading={downloading === r.key}
                  onClick={() => onDownload(r.key, "xlsx")}
                >
                  XLSX
                </Button>
                <Button loading={downloading === r.key} onClick={() => onDownload(r.key, "csv")}>
                  CSV
                </Button>
              </Space>
            </Space>
          </Card>
        ))}
      </Space>
    </PageTransition>
  );
};

export default Reports;
